import type { DocHandle, DocumentId, Repo } from "@automerge/automerge-repo";
import { Container, Graphics } from "pixi.js";
import { log } from "@freqhole/reliquary/utils";
import type { SkeinTheme } from "../theme/skein-theme";
import type { KeyboardDriver } from "../widgets/keyboard-driver";
import { createWidgetDoc } from "../widgets/widget-doc";
import { markResolved, resolveDocReadyCached, watchDocReady } from "../p2p/doc-ready";
import { computeDocHistoryStats } from "../p2p/doc-history-stats";
import type { WidgetRegistry } from "../widgets/widget-registry";
import type { WidgetController, WidgetDoc, WidgetMountContext } from "../widgets/widget-types";
import type { CanvasDocument, WidgetEntry } from "./canvas-doc";
import type { CanvasStore } from "./canvas-store";
import { createCrashedPlaceholder } from "./crashed-placeholder";
import { FocusStack } from "./focus-stack";
import type { InputRouter } from "./input-router";
import type { BreadcrumbItem, Toolbar } from "./toolbar";
import type { Viewport } from "./viewport";
import { WidgetFrame } from "./widget-frame";
import { copySelectionToClipboard, pasteClipboardIntoStore, hasClipboardContent, clearClipboard, isCopyable } from "./widget-clipboard";
import { showContextMenu, dismissContextMenu, type ContextMenuItem } from "./context-menu";

/**
 * stamp `ownerCanvasId` onto a widget doc that predates this stamping
 * mechanism (see canvas-scoped-share-policy.ts's module doc comment,
 * "rule 2"). a widget doc missing this field permanently fails that
 * policy's rule 3 ("ready, no .acl, no ownerCanvasId — denied, no
 * fallback") and can never sync to a peer who doesn't already have it —
 * calling this once, whenever the doc is next opened by a peer who already
 * has it, permanently repairs it (the write syncs to every other peer via
 * the normal automerge propagation, same as any other doc change).
 *
 * a no-op if the field is already present.
 */
export function backfillOwnerCanvasId(widgetDocHandle: DocHandle<any>, canvasDocumentId: DocumentId): void {
  const rawDoc = widgetDocHandle.doc();
  if (rawDoc && !("ownerCanvasId" in rawDoc)) {
    widgetDocHandle.change((d: Record<string, unknown>) => {
      d.ownerCanvasId = canvasDocumentId;
    });
  }
}

/** snapshot of positions at the start of a batch drag */
interface BatchDragState {
  /** the widget that initiated the drag */
  draggedId: string;
  /** starting positions of all other selected widgets (keyed by widget id) */
  startPositions: Map<string, { x: number; y: number }>;
}

/**
 * a live widget tracked by the widget manager.
 * contains everything needed to update or tear down a mounted widget.
 */
export interface LiveWidget {
  entry: WidgetEntry;
  ctrl: WidgetController;
  frame: WidgetFrame;
  crashed: boolean;
  /** the zod-validated doc facade, or null for stateless/crashed widgets */
  widgetDoc: WidgetDoc<any> | null;
  /** the raw automerge DocHandle backing `widgetDoc`, or null for
   *  stateless/crashed widgets or ones using a non-automerge doc override
   *  (e.g. SqliteSocialDoc) — kept alongside the zod facade specifically
   *  for compaction (`getWidgetDocHandle()` below), which needs the raw
   *  handle to read `A.stats()`/pass to `compactDoc()`. */
  docHandle: DocHandle<any> | null;
}

/**
 * the widget manager is the core bridge between the canvas store (data),
 * the widget registry / factories (rendering), and the pixi stage.
 *
 * it subscribes to canvas document changes and mounts, unmounts,
 * repositions, and resizes widgets as the document evolves.
 *
 * it also coordinates with the input router to handle mode changes
 * and widget selection, and provides frame callbacks for drag/resize/close/collapse.
 */
export class WidgetManager {
  private readonly store: CanvasStore;
  private readonly registry: WidgetRegistry;
  /** the FULL, regular-canvas widget registry — always this, regardless of
   *  which (possibly narrower, e.g. narthex's) registry `this.registry`
   *  itself is. needed for any cross-canvas operation that must resolve
   *  an ARBITRARY widget type on some OTHER canvas (e.g. duplicating a
   *  canvas-card's linked canvas, see canvas-duplicate.ts) — importing
   *  `createTestRegistry` directly from `widgets/index.ts` at the call
   *  site (canvas-card.ts, widget-clipboard.ts) instead of threading it
   *  down from here created real circular imports back into that same
   *  barrel file (`npm run lint:circular` caught this in CI), since it's
   *  the file that registers canvas-card.ts/widget-clipboard.ts's own
   *  consumers in the first place. */
  private readonly crossCanvasRegistry: WidgetRegistry;
  private readonly repo: Repo;
  private readonly stage: Container;
  private readonly theme: SkeinTheme;
  private readonly inputRouter: InputRouter;
  private readonly keyboard: KeyboardDriver;
  private readonly canvasElement: HTMLCanvasElement;
  private readonly stageBg: Graphics;

  private readonly liveWidgets = new Map<string, LiveWidget>();
  private readonly mountingIds = new Set<string>();
  private unsubs: (() => void)[] = [];

  /** background `watchDocReady()` cancel functions, one per widget ID
   *  currently showing a crashed placeholder because its per-widget doc
   *  failed to resolve in time (see `mountWidget()`'s `entry.docId` catch
   *  branch). lets the widget self-heal — remount for real — the moment
   *  the doc actually becomes ready, rather than staying permanently
   *  crashed until the whole canvas is torn down and remounted (a real
   *  reported bug: right after a knock/access-request was approved, the
   *  very first navigation into that canvas crashed every widget with
   *  "document unavailable" — the underlying ACL update hadn't finished
   *  propagating to whichever peer was serving the widget docs yet, but
   *  `resolveDocReadyCached()`'s bounded ~15s wait gave up long before it
   *  did; navigating away and back, or reloading, "fixed" it only because
   *  that gave the race enough real time to resolve before the *next*
   *  mount attempt, not because either action did anything special).
   *  cancelled and removed in `unmountWidget()` so a stale watcher never
   *  fires for a widget that's since been removed or replaced. */
  private readonly crashedRetryWatchers = new Map<string, () => void>();

  /** one-shot callbacks to fire the next time a given widget id finishes
   *  mounting (including a reconcile()-driven remount after its `docId`
   *  changed, e.g. compaction) -- see `onNextMount()`'s own doc comment. */
  private readonly nextMountWatchers = new Map<string, Set<() => void>>();

  /** optional hook called before a widget is permanently removed.
   *  receives the widget entry and the repo so callers can clean up
   *  linked documents (e.g. deleting a canvas-card's linked canvas). */
  private beforeRemoveHook: ((entry: WidgetEntry, repo: Repo) => void | Promise<void>) | null =
    null;

  /** optional doc overrides — if a widget ID is in this map, its doc will be
   *  used instead of creating one from automerge. used to inject SqliteSocialDoc
   *  for the social widget in tauri mode. */
  private readonly docOverrides = new Map<string, WidgetDoc<any>>();

  /** batch drag state — non-null while a multi-widget drag is in progress */
  private batchDrag: BatchDragState | null = null;

  /** cached stage background bounds to avoid unnecessary redraws */
  private lastStageBounds = { x: 0, y: 0, w: 0, h: 0 };

  /** the widget ID of the drop target currently being hovered during a drag, or null */
  private activeDropTarget: string | null = null;

  /** last known pointer world position during a drag — used for drop-target
   *  hit testing instead of the dragged widget's own (possibly oversized)
   *  center point, see widget-frame.ts's onDragPointerMove */
  private lastDragPointer: { x: number; y: number } | null = null;

  /** focus stack for maximize / restore navigation */
  private readonly focusStack = new FocusStack();

  /** widget IDs that were un-nested from the currently maximized widget.
   *  these should remain visible alongside the maximized widget. */
  private readonly maximizedEscapees = new Set<string>();

  /** viewport reference for saving/restoring camera state during maximize */
  private viewport: Viewport | null = null;

  /** current viewport zoom, kept in sync so newly-mounted frames start with
   *  the right resize-handle scale (see WidgetFrame.setZoom()). */
  private currentZoom = 1;

  /** toolbar reference for pushing breadcrumb updates */
  private toolbar: Toolbar | null = null;

  /** callback to navigate home (narthex) — used as the first breadcrumb */
  private onNavigateHome: (() => void) | null = null;

  /** ancestor canvas crumbs (narthex -> ... -> immediate parent), supplied by
   *  the app-level router so it can track cross-canvas navigation history
   *  (see boot.ts's `navHistory`). rendered between the "narthex" crumb and
   *  this canvas's own title crumb — see updateBreadcrumbs(). */
  private ancestorCrumbs: BreadcrumbItem[] = [];

  /** listeners notified on every tick of a live drag (own or batch) — see
   *  `onLiveMove()`. lets external UI anchored to a widget's screen
   *  position (the property tray, DOM text-input overlays) follow the
   *  frame live instead of only updating once the drag commits to the
   *  store (which only happens on release). */
  private liveMoveListeners: Array<() => void> = [];

  constructor(
    store: CanvasStore,
    registry: WidgetRegistry,
    crossCanvasRegistry: WidgetRegistry,
    repo: Repo,
    stage: Container,
    theme: SkeinTheme,
    inputRouter: InputRouter,
    keyboard: KeyboardDriver,
    canvasElement: HTMLCanvasElement,
    stageBg: Graphics
  ) {
    this.store = store;
    this.registry = registry;
    this.crossCanvasRegistry = crossCanvasRegistry;
    this.repo = repo;
    this.stage = stage;
    this.theme = theme;
    this.inputRouter = inputRouter;
    this.keyboard = keyboard;
    this.canvasElement = canvasElement;
    this.stageBg = stageBg;
    store.setDropTargetResolver((draggedId, worldX, worldY) => this.tryDropWidgetAt(draggedId, worldX, worldY));
  }

  /** register a hook that fires before a widget is permanently removed.
   *  the hook receives the widget entry (with docId, type, props) and the
   *  repo instance so it can open and delete linked documents. */
  setBeforeRemoveHook(hook: (entry: WidgetEntry, repo: Repo) => void | Promise<void>): void {
    this.beforeRemoveHook = hook;
  }

  /** register a doc override for a specific widget ID. must be called before
   *  start() so the override is available when widgets are first mounted. */
  setDocOverride(widgetId: string, doc: WidgetDoc<any>): void {
    this.docOverrides.set(widgetId, doc);
  }

  /** set the viewport reference so maximize/restore can save and restore camera state */
  setViewport(viewport: Viewport): void {
    this.viewport = viewport;
    this.currentZoom = viewport.zoom;
    viewport.onZoomChange((zoom) => {
      this.currentZoom = zoom;
      for (const live of this.liveWidgets.values()) {
        live.frame.setZoom(zoom);
      }
    });
  }

  /** set the toolbar reference so breadcrumbs can be updated on maximize/restore */
  setToolbar(toolbar: Toolbar): void {
    this.toolbar = toolbar;
  }

  /** set the navigate-home callback (shown as the "narthex" breadcrumb) */
  setNavigateHome(handler: (() => void) | null): void {
    this.onNavigateHome = handler;
  }

  /** set the ancestor canvas crumbs (narthex router history) and refresh the
   *  breadcrumb bar immediately — see the `ancestorCrumbs` field doc comment. */
  setAncestorCrumbs(crumbs: BreadcrumbItem[]): void {
    this.ancestorCrumbs = crumbs;
    this.updateBreadcrumbs();
  }

  /**
   * start the widget manager. reads all current widgets from the store
   * and mounts them, then subscribes to future changes, mode changes,
   * and selection changes.
   */
  start(): void {
    // enable z-index sorting on the stage so widget zIndex values work
    this.stage.sortableChildren = true;

    // mount everything currently in the document
    this.reconcile(this.store.doc());

    // subscribe to future document changes
    const unsubStore = this.store.onChange((doc) => {
      this.reconcile(doc);
    });
    this.unsubs.push(unsubStore);

    // subscribe to single-selection changes (backward compat for property tray etc.)
    const unsubSelection = this.inputRouter.onSelectionChange((_selectedId) => {
      // frame highlighting is driven by multi-selection below;
      // this listener kept for any future single-selection-only consumers.
    });
    this.unsubs.push(unsubSelection);

    // subscribe to multi-selection changes — update frame selection and multi-select state
    const unsubMultiSelection = this.inputRouter.onMultiSelectionChange((ids) => {
      const isMulti = ids.size > 1;
      for (const [id, live] of this.liveWidgets) {
        live.frame.setSelected(ids.has(id));
        live.frame.setMultiSelected(isMulti && ids.has(id));
      }
      // on touch, clear any active touch-hover toolbar when everything is deselected
      // (e.g. user tapped empty canvas or pressed Escape)
      if (ids.size === 0) {
        WidgetFrame.clearTouchHover();
      }
    });
    this.unsubs.push(unsubMultiSelection);

    // wire up the delete handler on the input router
    this.inputRouter.setDeleteHandler((id) => {
      if (this.store.isLocalViewer()) return;
      this.store.removeWidget(id);
    });

    // wire up z-order handlers on the input router
    // ] = bring to front, [ = send to back
    this.inputRouter.setBringForwardHandler((id) => {
      this.store.bringToFront(id);
      this.updateLayerInfo();
    });
    this.inputRouter.setSendBackwardHandler((id) => {
      this.store.sendToBack(id);
      this.updateLayerInfo();
    });

    // wire up Cmd/Ctrl+C / Cmd/Ctrl+V — see widget-clipboard.ts's own doc
    // comment for why the clipboard itself lives outside this class (it
    // must survive navigating to a different canvas, this class doesn't).
    this.inputRouter.setCopyHandler((ids) => {
      copySelectionToClipboard(this.store, this.registry, ids).catch((err) => {
        log.debug("widget-manager.clipboard", "copy failed (non-fatal):", err);
      });
    });
    this.inputRouter.setPasteHandler(() => {
      if (this.store.isLocalViewer()) return;
      pasteClipboardIntoStore(this.store, this.crossCanvasRegistry)
        .then((result) => {
          if (result.pasted.length > 0) this.inputRouter.selectWidgets(result.pasted);
        })
        .catch((err) => {
          log.debug("widget-manager.clipboard", "paste failed (non-fatal):", err);
        });
    });

    // right-click: "Copy" (selection) / "Paste here" (clipboard, at the
    // clicked world position) — see context-menu.ts/clipboard-cursor.ts.
    this.canvasElement.addEventListener("contextmenu", this.handleContextMenu);

    this.updateBreadcrumbs();
  }

  // --- focus stack (maximize / restore) ---

  /** maximize a widget to fill the viewport. pushes onto the focus stack. */
  maximize(widgetId: string): void {
    const live = this.liveWidgets.get(widgetId);
    if (!live || this.focusStack.hasWidget(widgetId)) return;

    const wasEmpty = this.focusStack.isEmpty;

    this.maximizedEscapees.clear();

    // save current viewport state so we can restore later
    const savedViewport = this.viewport
      ? { x: this.viewport.cameraX, y: this.viewport.cameraY, zoom: this.viewport.zoom }
      : { x: 0, y: 0, zoom: 1 };

    this.focusStack.push({
      widgetId,
      savedViewport,
      savedSize: { width: live.entry.width, height: live.entry.height },
    });

    // hide all other widget frames (including any previously maximized widget)
    for (const [id, other] of this.liveWidgets) {
      if (id !== widgetId) {
        other.frame.root.visible = false;
      }
    }

    // reset viewport to origin at 1x zoom
    if (this.viewport) {
      this.viewport.resetView();
    }

    // entering maximized mode for the first time (not a nested maximize of
    // one widget over another) — lock the camera in place and get the
    // toolbar out of the way; there's no room for it and its buttons don't
    // apply while a single widget fills the viewport.
    if (wasEmpty) {
      this.viewport?.setLocked(true);
      this.toolbar?.setVisible(false);
    }

    // enter maximized mode on the frame (hides chrome, disables drag)
    live.frame.setMaximized(true);

    // position frame at origin, reserving room at the top for the header
    // (now always shown while maximized — see widget-frame.ts) so it
    // doesn't overlay the content or land above the visible viewport.
    const { width, height } = this.getViewportSize();
    const hdr = live.frame.headerHeight;
    const contentHeight = Math.max(0, height - hdr);
    live.frame.setPosition(0, hdr);
    live.frame.updateSize(width, contentHeight);
    if (live.ctrl.resize) {
      try {
        live.ctrl.resize(width, contentHeight);
      } catch (err) {
        console.warn(`widget ${widgetId} threw during maximize resize:`, err);
      }
    }

    // notify the widget so it can adapt its rendering
    if (live.ctrl.setMaximized) {
      try {
        live.ctrl.setMaximized(true);
      } catch (err) {
        console.warn(`widget ${widgetId} threw during setMaximized(true):`, err);
      }
    }

    // update breadcrumbs to reflect maximized state
    this.updateBreadcrumbs();
  }

  /** restore the most recently maximized widget. pops the focus stack. */
  restore(): void {
    const entry = this.focusStack.pop();
    if (!entry) return;

    const live = this.liveWidgets.get(entry.widgetId);

    if (live) {
      // notify the widget it's leaving maximized mode
      if (live.ctrl.setMaximized) {
        try {
          live.ctrl.setMaximized(false);
        } catch (err) {
          console.warn(`widget ${entry.widgetId} threw during setMaximized(false):`, err);
        }
      }

      // restore frame chrome and original size
      live.frame.setMaximized(false);
      live.frame.setPosition(live.entry.x, live.entry.y);
      live.frame.updateSize(entry.savedSize.width, entry.savedSize.height);
      if (live.ctrl.resize) {
        try {
          live.ctrl.resize(entry.savedSize.width, entry.savedSize.height);
        } catch (err) {
          console.warn(`widget ${entry.widgetId} threw during restore resize:`, err);
        }
      }
    }

    if (this.focusStack.isEmpty) {
      // stack fully empty — show all widgets, restore original viewport
      for (const other of this.liveWidgets.values()) {
        other.frame.root.visible = true;
      }
      if (this.viewport) {
        this.viewport.setLocked(false);
        this.viewport.zoomTo(entry.savedViewport.zoom);
        this.viewport.panTo(entry.savedViewport.x, entry.savedViewport.y);
      }
      this.toolbar?.setVisible(true);
    } else {
      // still a maximized widget underneath — show only that one
      const parent = this.focusStack.peek()!;
      const parentLive = this.liveWidgets.get(parent.widgetId);
      if (parentLive) {
        parentLive.frame.root.visible = true;
      }
      // hide the widget we just restored (it goes back behind the parent)
      if (live) {
        live.frame.root.visible = false;
      }
    }

    this.maximizedEscapees.clear();
    this.updateBreadcrumbs();
  }

  /** whether any widget is currently maximized */
  get isMaximized(): boolean {
    return !this.focusStack.isEmpty;
  }

  /** current viewport size in CSS pixels */
  private getViewportSize(): { width: number; height: number } {
    return {
      width: this.canvasElement.clientWidth,
      height: this.canvasElement.clientHeight,
    };
  }

  /** restore all maximized widgets back to the canvas level */
  private restoreAll(): void {
    while (!this.focusStack.isEmpty) {
      this.restore();
    }
  }

  /** restore maximized widgets until the focus stack has `targetDepth` entries.
   *  e.g., targetDepth=1 means keep only the bottom entry (the first maximized widget). */
  private restoreTo(targetDepth: number): void {
    while (this.focusStack.depth > targetDepth) {
      this.restore();
    }
  }

  /** recompute and push breadcrumbs to the toolbar based on the current focus stack */
  private updateBreadcrumbs(): void {
    if (!this.toolbar) return;

    const crumbs: BreadcrumbItem[] = [];

    // "narthex" crumb — always first when navigateHome is available
    if (this.onNavigateHome) {
      const handler = this.onNavigateHome;
      crumbs.push({ label: "narthex", onClick: () => handler() });
    }

    // ancestor canvas crumbs (cross-canvas navigation history) — bounded by
    // the router (boot.ts) to at most one entry (the immediate parent) plus
    // a leading non-clickable "..." when there's deeper history, so this
    // never grows unbounded regardless of how many canvases were visited.
    crumbs.push(...this.ancestorCrumbs);

    // canvas title crumb — on the narthex (no onNavigateHome), skip if untitled
    const canvasTitle = this.store.metadata().title;
    const showCanvasCrumb = canvasTitle || this.onNavigateHome;
    const displayTitle = canvasTitle || "untitled canvas";

    if (!showCanvasCrumb) {
      // narthex with no title — no breadcrumbs needed
      this.toolbar.setBreadcrumbs(crumbs);
      return;
    }

    if (this.focusStack.isEmpty) {
      // not maximized — canvas name is the current (non-clickable) context
      crumbs.push({ label: displayTitle });
    } else {
      // maximized — canvas name is clickable to restore all
      crumbs.push({ label: displayTitle, onClick: () => this.restoreAll() });

      // add a crumb for each focus stack entry (bottom to top)
      const entries = this.focusStack.entries;
      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        const live = this.liveWidgets.get(entry.widgetId);
        const factory = this.registry.get(live?.entry.type ?? "");
        const label = live?.entry.title || factory?.metadata.name || "widget";
        const isLast = i === entries.length - 1;

        if (isLast) {
          // current maximized widget — non-clickable
          crumbs.push({ label });
        } else {
          // intermediate — clicking restores to this level
          const targetDepth = i + 1;
          crumbs.push({ label, onClick: () => this.restoreTo(targetDepth) });
        }
      }
    }

    this.toolbar.setBreadcrumbs(crumbs);
  }

  /**
   * mount a single widget onto the stage.
   *
   * handles two scenarios:
   * - new widget (no docId): creates a fresh automerge doc and stores the
   *   docId back into the canvas document so other peers can find it.
   * - synced widget (has docId): uses repo.find() to locate the existing
   *   per-widget document and waits for it to be ready before mounting.
   */
  private async mountWidget(entry: WidgetEntry): Promise<void> {
    // guard against re-entrant mounts. setDocId() inside this method
    // triggers a synchronous reconcile() callback which sees the widget
    // as "new" (not yet in liveWidgets) and calls mountWidget() again.
    // the Set is checked here and in reconcile() to prevent duplicates.
    if (this.mountingIds.has(entry.id)) return;
    this.mountingIds.add(entry.id);

    const factory = this.registry.get(entry.type);

    // if the factory is not found, mount a crashed placeholder
    if (!factory) {
      this.mountingIds.delete(entry.id);
      this.mountCrashed(entry, `unknown widget type: "${entry.type}"`);
      return;
    }

    // build the per-widget document facade
    let doc: WidgetDoc<any>;
    // the raw handle backing `doc`, if any — see `LiveWidget.docHandle`'s
    // own doc comment for why this is tracked separately from `doc`.
    let rawDocHandle: DocHandle<any> | null = null;

    // check for doc override (e.g., SqliteSocialDoc in tauri mode)
    if (this.docOverrides.has(entry.id)) {
      doc = this.docOverrides.get(entry.id)!;
    } else if (factory.schema) {
      let widgetDocHandle: DocHandle<any>;

      if (entry.docId) {
        // this widget already has a per-widget document (synced from another
        // peer, or restored from persistence). find it in the repo and wait
        // for it to be available.
        try {
          const found = await resolveDocReadyCached<any>(this.repo, entry.docId as DocumentId);
          if (!found) throw new Error("timed out waiting for widget doc to become ready");
          widgetDocHandle = found;
        } catch (err) {
          console.warn(`failed to find widget doc ${entry.docId} for widget ${entry.id}:`, err);
          this.mountingIds.delete(entry.id);
          this.mountCrashed(
            entry,
            `widget doc not available: ${err instanceof Error ? err.message : String(err)}`
          );
          // keep waiting in the background (no timeout) — the doc can
          // still become ready later (e.g. once an ACL update finishes
          // propagating to whichever peer serves it), and reconcile()
          // never retries a widget that's already in `liveWidgets` on its
          // own, so without this the crashed placeholder above would be
          // permanent until the canvas is torn down and remounted.
          const cancel = watchDocReady(this.repo, entry.docId as DocumentId, () => {
            this.crashedRetryWatchers.delete(entry.id);
            if (this.liveWidgets.get(entry.id)?.crashed) {
              // watchDocReady just confirmed the doc is actually ready, but
              // resolveDocReadyCached's negative-result cache doesn't know
              // that yet (it can still be inside the cooldown window from
              // the failed attempt above) — without clearing it here,
              // mountWidget()'s retry would short-circuit back to null
              // immediately, re-crash, and re-arm another watchDocReady on
              // this same already-ready handle, which fires instantly and
              // repeats forever (a tight infinite loop, no delay between
              // iterations, observed to crash the tab).
              markResolved(entry.docId as string);
              this.unmountWidget(entry.id, false);
              void this.mountWidget(entry);
            }
          });
          this.crashedRetryWatchers.set(entry.id, cancel);
          return;
        }

        // backfill `ownerCanvasId` on a pre-existing widget doc that predates
        // this stamping mechanism (see the "new widget" branch below and
        // canvas-scoped-share-policy.ts's module doc comment) — without it,
        // the doc permanently fails that policy's rule 3 ("ready, no .acl,
        // no ownerCanvasId — denied, no fallback") and can never sync to a
        // peer who doesn't already have it.
        backfillOwnerCanvasId(widgetDocHandle, this.store.handle.documentId);

        // unconditional (not gated behind localStorage.logLevel) bloat
        // check — a widget doc this large is itself a likely multi-second
        // main-thread stall right at mount (A.loadIncremental is a
        // synchronous WASM call), which is exactly the kind of freeze a
        // user has, at most, a few moments to notice before the tab locks
        // up — logDocHistoryStats()'s debug-gated log arrives too late to
        // help in that window, so this prints eagerly instead.
        //
        // deliberately the CHEAP form (no `includeExpensive`) — a real
        // freeze capture showed the expensive form (A.save()/decodeChange
        // loop for maxChangeOps) itself taking 5+ seconds for one already-
        // bloated doc, with no upper bound for a worse one, making this
        // diagnostic a bigger stall than the thing it was meant to catch.
        // the deep numbers are still available on demand via
        // `diagnoseDocHistory()`/`window.__skeinDiagnose()`.
        const bloatStats = computeDocHistoryStats(widgetDocHandle);
        if (bloatStats && bloatStats.numOps > 20_000) {
          console.warn(
            `[widget-manager] widget ${entry.id} (${factory.metadata.name}, doc ${widgetDocHandle.documentId}) ` +
              `has a large automerge history — numChanges: ${bloatStats.numChanges}, numOps: ${bloatStats.numOps} ` +
              `(run window.__skeinDiagnose() for maxChangeOps/savedBytes)`
          );
        }
      } else {
        // new widget with no existing document — create one and persist the
        // docId back into the canvas document so other peers can sync it.
        // merge entry.props into schema defaults so callers can seed initial
        // state (e.g., canvasDocId for narthex canvas-card widgets — note
        // this is a DIFFERENT field than the `ownerCanvasId` stamped
        // below: canvas-card's own `canvasDocId` is real widget data (the
        // canvas a narthex card *links to*), whereas `ownerCanvasId`
        // records the canvas this widget doc *physically lives on* — see
        // canvas-scoped-share-policy.ts's module doc comment for why they
        // can't share a name).
        const defaults = factory.schema.parse(entry.props ?? {});
        // stamp the owning canvas's own doc id directly into the raw
        // automerge content (bypassing the schema, which strips anything
        // it doesn't declare — no widget type needs to know this field
        // exists). this is what canvas-scoped-share-policy.ts's `access`/
        // `announce` policy uses to resolve "which canvas's `.acl` governs
        // this widget doc" without any reverse-lookup guessing.
        const initialContent = {
          ...(defaults as Record<string, unknown>),
          ownerCanvasId: this.store.handle.documentId,
        };
        widgetDocHandle = this.repo.create(initialContent);
        this.store.setDocId(entry.id, widgetDocHandle.documentId);
      }

      doc = createWidgetDoc(factory.schema, widgetDocHandle, factory.migrate);
      rawDocHandle = widgetDocHandle;
    } else {
      // stateless widget: no-op document facade
      doc = {
        get current() {
          return {};
        },
        // eslint-disable-next-line no-empty-function -- intentional no-op for stateless widgets
        change() {},
        on() {
          return () => {};
        },
      };
    }

    // resolve the widget name from factory metadata
    const widgetName = factory.metadata.name;

    // build callbacks that close over this entry's id
    const callbacks = this.createFrameCallbacks(entry.id, entry.type);

    // create the pixi frame
    const frame = new WidgetFrame(entry, widgetName, this.theme, callbacks);
    frame.setClosable(factory.metadata.closable === true);
    frame.setZoom(this.currentZoom);

    // if this widget is the currently selected one, mark it
    if (this.inputRouter.selectedWidgetId === entry.id) {
      frame.setSelected(true);
    }

    // build the mount context
    const ctx: WidgetMountContext = {
      doc,
      width: entry.width,
      height: entry.height,
      keyboard: this.keyboard,
      canvasElement: this.canvasElement,
      canvasStore: this.store,
      crossCanvasRegistry: this.crossCanvasRegistry,
      widgetId: entry.id,
      setHeaderActions: (actions) => {
        frame.setCustomActions(actions);
      },
      setTitleProgress: (progress) => {
        frame.setTitleProgress(progress);
      },
    };

    let ctrl: WidgetController;
    try {
      ctrl = factory.create(ctx);
    } catch (err) {
      console.warn(
        `widget factory "${entry.type}" threw during create for widget ${entry.id}:`,
        err
      );
      // tear down the frame we already created
      frame.destroy();
      this.mountingIds.delete(entry.id);
      this.mountCrashed(
        entry,
        `factory threw: ${err instanceof Error ? err.message : String(err)}`
      );
      return;
    }

    // apply initial header actions if the widget declared any
    if (ctrl.headerActions) {
      frame.setCustomActions(ctrl.headerActions);
    }
    if (ctrl.titleProgress !== null && ctrl.titleProgress !== undefined) {
      frame.setTitleProgress(ctrl.titleProgress);
    }

    // add the widget's container into the frame's content area
    frame.contentContainer.addChild(ctrl.container);

    // add the frame root to the stage
    this.stage.addChild(frame.root);

    // track it
    this.liveWidgets.set(entry.id, {
      entry: { ...entry },
      ctrl,
      frame,
      crashed: false,
      widgetDoc: factory.schema ? doc : null,
      docHandle: rawDocHandle,
    });
    this.mountingIds.delete(entry.id);
    this.fireNextMountWatchers(entry.id);
  }

  /**
   * mount a crashed placeholder for a widget that failed to load.
   */
  private mountCrashed(entry: WidgetEntry, reason: string): void {
    const callbacks = this.createFrameCallbacks(entry.id, entry.type);
    const frame = new WidgetFrame(entry, "crashed", this.theme, callbacks);
    frame.setZoom(this.currentZoom);
    const ctrl = createCrashedPlaceholder(entry.width, entry.height, reason, this.theme);

    frame.contentContainer.addChild(ctrl.container);
    this.stage.addChild(frame.root);

    this.liveWidgets.set(entry.id, {
      entry: { ...entry },
      ctrl,
      frame,
      crashed: true,
      widgetDoc: null,
      docHandle: null,
    });
    this.fireNextMountWatchers(entry.id);
  }

  /** the raw automerge DocHandle backing `widgetId`'s own document, if any
   *  — `null` for a stateless/crashed widget, a non-automerge doc override,
   *  or an id not currently mounted. used by the property tray's "compact
   *  this doc" button to read stats and pass to `compactDoc()` (see
   *  `docs/animaniac-doc-compaction-plan.md`). */
  getWidgetDocHandle(widgetId: string): DocHandle<any> | null {
    return this.liveWidgets.get(widgetId)?.docHandle ?? null;
  }

  /** call `callback` exactly once, the next time `widgetId` finishes an
   *  async mount -- including a reconcile()-driven remount triggered by its
   *  `docId` changing underneath it (e.g. compaction). used by
   *  property-tray.ts to rebuild its controls against the fresh doc facade
   *  once a remount actually completes, instead of continuing to operate
   *  on a stale, orphaned `DocHandle` (or guessing at remount timing via
   *  polling). returns an unsubscribe function. */
  onNextMount(widgetId: string, callback: () => void): () => void {
    let watchers = this.nextMountWatchers.get(widgetId);
    if (!watchers) {
      watchers = new Set();
      this.nextMountWatchers.set(widgetId, watchers);
    }
    watchers.add(callback);
    return () => watchers!.delete(callback);
  }

  /** fires and clears every pending onNextMount() callback for `widgetId`
   *  -- called once a mount (including a remount) actually completes. */
  private fireNextMountWatchers(widgetId: string): void {
    const watchers = this.nextMountWatchers.get(widgetId);
    if (!watchers || watchers.size === 0) return;
    this.nextMountWatchers.delete(widgetId);
    for (const cb of watchers) {
      try {
        cb();
      } catch (err) {
        console.warn(`onNextMount callback threw for widget ${widgetId}:`, err);
      }
    }
  }

  /**
   * close a widget: deselect, cascade-delete descendants, remove from store.
   * used by the property tray delete button and internal close logic.
   */
  closeWidget(widgetId: string): void {
    // viewers cannot delete widgets — this is the single funnel point for
    // delete (toolbar delete button, property tray, keyboard shortcut all
    // route through here).
    if (this.store.isLocalViewer()) return;

    if (this.inputRouter.selectedWidgetIds.has(widgetId)) {
      this.inputRouter.selectWidget(null);
    }

    // check if the widget factory wants to handle close itself
    const beforeCloseEntry = this.store.getWidget(widgetId);
    const beforeCloseFactory = beforeCloseEntry ? this.registry.get(beforeCloseEntry.type) : null;
    if (beforeCloseFactory?.onBeforeClose) {
      const handled = beforeCloseFactory.onBeforeClose(widgetId, this.store);
      if (handled) return;
    }

    const collectDescendants = (id: string): string[] => {
      const children = this.store.getChildren(id);
      return children.flatMap((c) => [c.id, ...collectDescendants(c.id)]);
    };
    const descendants = collectDescendants(widgetId);

    // check if this widget type preserves children on close (e.g. trash can).
    // if so, un-parent descendants instead of cascade-deleting them.
    const entry = this.store.getWidget(widgetId);
    const factory = entry ? this.registry.get(entry.type) : null;
    const preserve = factory?.metadata.preserveChildren === true;

    if (preserve) {
      for (const descendantId of descendants) {
        this.store.setParentId(descendantId, null);
      }
    } else {
      for (const descendantId of descendants) {
        this.store.removeWidget(descendantId);
      }
    }
    this.store.removeWidget(widgetId);
  }

  /**
   * create the callback object for a widget frame.
   * each callback closes over the widget id and delegates to the store or input router.
   */
  private createFrameCallbacks(widgetId: string, widgetType?: string) {
    return {
      isReadOnly: () => this.store.isLocalViewer(),
      onSelect: () => {
        this.inputRouter.selectWidget(widgetId);
      },
      onShiftSelect: () => {
        this.inputRouter.toggleWidgetInSelection(widgetId);
      },
      onDeselect: () => {
        this.inputRouter.selectWidget(null);
      },
      onTogglePropTray: () => {
        if (this.inputRouter.selectedWidgetId === widgetId) {
          this.inputRouter.selectWidget(null);
        } else {
          this.inputRouter.selectWidget(widgetId);
        }
      },
      onMove: (x: number, y: number) => {
        if (this.store.isLocalViewer()) return;
        this.store.moveWidget(widgetId, x, y);
        this.updateStageBounds();
      },
      onResize: (width: number, height: number) => {
        if (this.store.isLocalViewer()) return;
        this.store.resizeWidget(widgetId, width, height);
        // also call ctrl.resize() on the live widget
        const live = this.liveWidgets.get(widgetId);
        if (live && live.ctrl.resize) {
          try {
            live.ctrl.resize(width, height);
          } catch (err) {
            console.warn(`widget ${widgetId} threw during resize callback:`, err);
          }
        }
        this.updateStageBounds();
      },
      onClose: () => {
        this.closeWidget(widgetId);
      },
      onCollapse: (collapsed: boolean) => {
        this.store.setCollapsed(widgetId, collapsed);
        const live = this.liveWidgets.get(widgetId);
        if (live) {
          live.frame.setCollapsed(collapsed);
        }
      },
      onMaximize: (() => {
        const factory = widgetType ? this.registry.get(widgetType) : null;
        if (factory?.metadata.maximizable === false) return undefined;
        return () => {
          this.maximize(widgetId);
        };
      })(),
      onRestore: () => {
        this.restore();
      },

      // batch drag support — only activates when multiple widgets are selected
      onDragStart: () => {
        this.activeDropTarget = null;
        this.handleBatchDragStart(widgetId);
      },
      onDragDelta: (dx: number, dy: number) => {
        this.handleBatchDragDelta(widgetId, dx, dy);
        // the dragged widget's own frame already moved (widget-frame.ts's
        // updateDrag()) — notify anything anchored to its live position.
        const live = this.liveWidgets.get(widgetId);
        try {
          live?.ctrl.onReposition?.();
        } catch (err) {
          console.warn(`widget ${widgetId} threw during onReposition callback:`, err);
        }
        this.notifyLiveMove();
      },
      onDragPointerMove: (worldX: number, worldY: number) => {
        this.lastDragPointer = { x: worldX, y: worldY };
        this.checkDropTargetHover(widgetId, worldX, worldY);
      },
      onDragEnd: () => {
        this.tryDropOnTarget(widgetId);
        this.lastDragPointer = null;
        this.handleBatchDragEnd(widgetId);
      },

      // z-order controls (from the layers flyout)
      onBringToFront: () => {
        this.store.bringToFront(widgetId);
        this.updateLayerInfo();
      },
      onBringForward: () => {
        this.store.bringForward(widgetId);
        this.updateLayerInfo();
      },
      onSendBackward: () => {
        this.store.sendBackward(widgetId);
        this.updateLayerInfo();
      },
      onSendToBack: () => {
        this.store.sendToBack(widgetId);
        this.updateLayerInfo();
      },
    };
  }

  // --- batch drag support ---

  /**
   * when a drag starts on a selected widget and multiple widgets are selected,
   * snapshot the starting positions of all other selected widgets so we can
   * move them in sync during the drag.
   */
  private handleBatchDragStart(draggedId: string): void {
    const selectedIds = this.inputRouter.selectedWidgetIds;
    if (selectedIds.size <= 1) {
      this.batchDrag = null;
      return;
    }

    const startPositions = new Map<string, { x: number; y: number }>();
    for (const id of selectedIds) {
      if (id === draggedId) continue;
      const live = this.liveWidgets.get(id);
      if (live) {
        startPositions.set(id, { x: live.frame.root.x, y: live.frame.root.y });
      }
    }

    this.batchDrag = { draggedId, startPositions };
  }

  /**
   * move all other selected widgets by the same delta as the dragged widget.
   * the dragged widget's frame moves itself — we only handle the others.
   */
  private handleBatchDragDelta(_draggedId: string, dx: number, dy: number): void {
    if (!this.batchDrag) return;

    for (const [id, startPos] of this.batchDrag.startPositions) {
      const live = this.liveWidgets.get(id);
      if (live) {
        live.frame.setPosition(startPos.x + dx, startPos.y + dy);
        try {
          live.ctrl.onReposition?.();
        } catch (err) {
          console.warn(`widget ${id} threw during onReposition callback:`, err);
        }
      }
    }
  }

  /**
   * subscribe to be notified on every tick of a live drag (own or
   * batch-dragged). returns an unsubscribe function. used by init.ts to
   * keep the property tray glued to the selected widget's live position
   * during a drag, instead of only repositioning once the drag ends and
   * commits to the store (see property-tray.ts's `repositionIfNeeded()`,
   * which is store-change-driven and therefore otherwise silent mid-drag).
   */
  onLiveMove(handler: () => void): () => void {
    this.liveMoveListeners.push(handler);
    return () => {
      this.liveMoveListeners = this.liveMoveListeners.filter((h) => h !== handler);
    };
  }

  private notifyLiveMove(): void {
    for (const handler of this.liveMoveListeners) {
      handler();
    }
  }

  /**
   * commit final positions of all batch-dragged widgets to the store.
   */
  private handleBatchDragEnd(_draggedId: string): void {
    if (!this.batchDrag) return;

    for (const [id] of this.batchDrag.startPositions) {
      const live = this.liveWidgets.get(id);
      if (live) {
        this.store.moveWidget(id, live.frame.root.x, live.frame.root.y);
      }
    }

    this.batchDrag = null;
    this.updateStageBounds();
  }

  /**
   * single-shot "is the drop point over some live widget's own drop
   * target" check, usable OUTSIDE the normal widget-frame-drag tracking
   * this class does for its own `checkDropTargetHover()`/
   * `tryDropOnTarget()` above (which both require the dragged widget to
   * already be a tracked, live top-level frame in `liveWidgets` — not
   * true for e.g. a bin's own drag-out-of-bin gesture, where the item is
   * still just bin-list data at the moment of release). registered onto
   * `CanvasStore` at construction time (see `setDropTargetResolver()`) so
   * a caller with only a `CanvasStore` reference (no direct access to
   * this manager) can still reach it — see `bin-drag.ts`'s own use.
   * returns true if some live widget's drop target claimed AND consumed
   * the drop; false otherwise (including "nothing hit-tested"), so the
   * caller can safely fall back to its own default drop behavior.
   */
  tryDropWidgetAt(draggedId: string, worldX: number, worldY: number): boolean {
    for (const [targetId, live] of this.liveWidgets) {
      if (targetId === draggedId) continue;
      if (!live.ctrl.dropTarget) continue;
      if (!live.ctrl.dropTarget.hitTest(worldX, worldY)) continue;
      const consumed = live.ctrl.dropTarget.onDrop(draggedId, worldX, worldY);
      log.debug("widget-manager.drop", "[DROP-DBG] tryDropWidgetAt (single-shot):", "draggedId:", draggedId, "targetType:", live.entry.type, "consumed:", consumed);
      live.ctrl.dropTarget.onLeave();
      if (consumed) return true;
    }
    return false;
  }

  /**
   * during a frame drag, check if the dragged widget is hovering over
   * any live widget that implements a drop target. if so, forward hover
   * events for visual feedback (e.g. bin slot highlighting).
   *
   * uses the pointer's own world position (from widget-frame.ts's
   * onDragPointerMove) rather than the dragged widget's center — a widget
   * much larger than the drop target (e.g. a doodle canvas dropped onto a
   * bin) would otherwise need its far-off center dragged inside the target
   * before a drop registered, which reads as "impossible to drop".
   */
  private checkDropTargetHover(draggedId: string, wx: number, wy: number): void {
    const draggedLive = this.liveWidgets.get(draggedId);
    if (!draggedLive) return;

    let foundTarget: string | null = null;

    for (const [targetId, live] of this.liveWidgets) {
      if (targetId === draggedId) continue;
      if (!live.ctrl.dropTarget) continue;

      if (live.ctrl.dropTarget.hitTest(wx, wy)) {
        live.ctrl.dropTarget.onHover(wx, wy, draggedId);
        foundTarget = targetId;
        break;
      }
    }

    // if we left the previous target, notify it
    if (this.activeDropTarget && this.activeDropTarget !== foundTarget) {
      const prev = this.liveWidgets.get(this.activeDropTarget);
      prev?.ctrl.dropTarget?.onLeave();
    }

    if (foundTarget !== this.activeDropTarget) {
      log.debug("widget-manager.drop", "[DROP-DBG] active drop target changed:", this.activeDropTarget, "->", foundTarget, "draggedId:", draggedId, "draggedType:", draggedLive.entry.type);
    }
    this.activeDropTarget = foundTarget;
  }

  /**
   * at the end of a frame drag, check if the widget should be dropped
   * onto an active drop target. returns true if the drop was consumed.
   */
  private tryDropOnTarget(draggedId: string): boolean {
    if (!this.activeDropTarget) {
      log.debug("widget-manager.drop", "[DROP-DBG] tryDropOnTarget: no active drop target at release", draggedId);
      return false;
    }

    const targetLive = this.liveWidgets.get(this.activeDropTarget);
    const draggedLive = this.liveWidgets.get(draggedId);

    if (!targetLive?.ctrl.dropTarget || !draggedLive) {
      log.debug(
        "widget-manager.drop",
        "[DROP-DBG] tryDropOnTarget: missing target/dragged live widget",
        "targetId:",
        this.activeDropTarget,
        "hasTargetDropTarget:",
        !!targetLive?.ctrl.dropTarget,
        "hasDraggedLive:",
        !!draggedLive
      );
      this.activeDropTarget = null;
      return false;
    }

    // prefer the last known pointer position (matches what was used to
    // find/highlight this target during the drag) — falls back to the
    // dragged widget's center if a pointer position was never recorded
    const wx = this.lastDragPointer?.x ?? draggedLive.frame.root.x + draggedLive.entry.width / 2;
    const wy = this.lastDragPointer?.y ?? draggedLive.frame.root.y + draggedLive.entry.height / 2;

    const consumed = targetLive.ctrl.dropTarget.onDrop(draggedId, wx, wy);
    log.debug(
      "widget-manager.drop",
      "[DROP-DBG] tryDropOnTarget: onDrop",
      "draggedType:",
      draggedLive.entry.type,
      "targetType:",
      targetLive.entry.type,
      "consumed:",
      consumed
    );

    // if the primary drop was consumed and there are other selected widgets
    // in the batch drag, drop those too (multi-drop into bin)
    if (consumed && this.batchDrag) {
      for (const [otherId] of this.batchDrag.startPositions) {
        const otherLive = this.liveWidgets.get(otherId);
        if (!otherLive) continue;
        const ox = otherLive.frame.root.x + otherLive.entry.width / 2;
        const oy = otherLive.frame.root.y + otherLive.entry.height / 2;
        // re-check drop target is still valid (previous drop might have destroyed it)
        if (!targetLive.ctrl.dropTarget) break;
        targetLive.ctrl.dropTarget.onDrop(otherId, ox, oy);
      }
      // clear batch drag state since all widgets were consumed
      this.batchDrag = null;
    }

    targetLive.ctrl.dropTarget?.onLeave();
    this.activeDropTarget = null;

    return consumed;
  }

  /**
   * unmount a single widget from the stage and clean up.
   *
   * when `permanent` is true (the default), the per-widget automerge doc is
   * also deleted from the repo / IndexedDB. this is the right behaviour when
   * a widget is removed from the canvas document (reconcile path).
   *
   * when `permanent` is false, the doc is left intact — used during navigation
   * teardown so that docs are still available when the canvas is re-opened.
   */
  private unmountWidget(id: string, permanent = true): void {
    const live = this.liveWidgets.get(id);
    if (!live) return;

    // cancel any pending crashed-placeholder retry watcher for this widget
    // (see `crashedRetryWatchers`'s doc comment) — it must never fire for
    // a widget that's since been removed or is about to be remounted for
    // real by the caller.
    this.crashedRetryWatchers.get(id)?.();
    this.crashedRetryWatchers.delete(id);

    try {
      live.ctrl.destroy();
    } catch (err) {
      console.warn(`widget ${id} threw during destroy:`, err);
    }

    live.frame.destroy();

    // only delete the per-widget automerge doc when the widget was permanently
    // removed from the canvas. during navigation teardown we keep the doc so
    // it can be found again when the canvas is re-mounted.
    if (permanent) {
      // fire the before-remove hook so callers can clean up linked docs
      if (this.beforeRemoveHook) {
        try {
          const result = this.beforeRemoveHook(live.entry, this.repo);
          // if the hook returns a promise, let it run but don't block teardown
          if (result && typeof (result as Promise<void>).catch === "function") {
            (result as Promise<void>).catch((err) => {
              console.warn(`beforeRemoveHook failed for widget ${id}:`, err);
            });
          }
        } catch (err) {
          console.warn(`beforeRemoveHook threw for widget ${id}:`, err);
        }
      }

      if (live.entry.docId) {
        this.repo.delete(live.entry.docId as DocumentId);
      }

      // a permanent removal means nothing will ever mount for this id
      // again -- unlike a transient (permanent=false) unmount (docId swap,
      // navigation teardown), where a watcher registered around the same
      // time is very much still waiting for the upcoming remount and must
      // survive regardless of listener dispatch order.
      this.nextMountWatchers.delete(id);
    }

    this.liveWidgets.delete(id);
  }

  /**
   * reconcile the live widget map against the current canvas document.
   * mounts new widgets, unmounts removed ones, and updates position/size
   * for widgets that changed.
   */
  private reconcile(doc: CanvasDocument): void {
    const docWidgetIds = new Set(Object.keys(doc.widgets));
    const liveWidgetIds = new Set(this.liveWidgets.keys());

    // find widgets that were removed from the document
    for (const id of liveWidgetIds) {
      if (!docWidgetIds.has(id)) {
        // if the removed widget was maximized, restore before unmounting
        if (this.focusStack.peek()?.widgetId === id) {
          this.restore();
        }
        this.unmountWidget(id);
      }
    }

    // find new widgets and update existing ones
    for (const [id, entry] of Object.entries(doc.widgets)) {
      // skip widgets nested inside a parent (the parent bin renders them)
      if (entry.parentId) {
        // if this widget was maximized and just got nested, restore first
        if (this.focusStack.peek()?.widgetId === id && liveWidgetIds.has(id)) {
          this.restore();
        }
        // if this widget was previously mounted and just gained a parentId,
        // unmount it — the parent widget takes over rendering.
        // use permanent=false so the automerge doc is preserved.
        if (liveWidgetIds.has(id)) {
          this.unmountWidget(id, false);
        }
        continue;
      }

      if (!liveWidgetIds.has(id) && !this.mountingIds.has(id)) {
        // new widget (or previously parented widget that lost its parentId) — mount it
        this.mountWidget(entry);
        // if we're in maximized mode, this widget likely just escaped from a bin —
        // keep it visible alongside the maximized widget
        if (!this.focusStack.isEmpty) {
          this.maximizedEscapees.add(id);
        }
      } else {
        // existing widget — check for position/size/zIndex/collapsed changes
        const live = this.liveWidgets.get(id);
        if (!live) continue; // still mounting asynchronously
        const prev = live.entry;

        // the widget's underlying doc was rotated (e.g. compaction gave it
        // a fresh, history-free doc id) — the mounted instance is still
        // bound to the OLD DocHandle and would otherwise keep rendering/
        // writing to it forever. checked before the maximized-skip branch
        // below since a docId change must take effect even while
        // maximized. `permanent=false` preserves whichever doc is now
        // current (the old one is simply no longer referenced).
        if (prev.docId !== entry.docId) {
          if (this.focusStack.peek()?.widgetId === id) {
            this.restore();
          }
          this.unmountWidget(id, false);
          void this.mountWidget(entry);
          continue;
        }

        // if this widget is currently maximized, snapshot the entry but skip
        // visual updates — maximize controls the frame position and size
        if (this.focusStack.peek()?.widgetId === id) {
          live.entry = { ...entry };
          continue;
        }

        // update position if changed
        if (prev.x !== entry.x || prev.y !== entry.y) {
          live.frame.setPosition(entry.x, entry.y);
        }

        // update z-index if changed
        if (prev.zIndex !== entry.zIndex) {
          live.frame.setZIndex(entry.zIndex);
        }

        // update size if changed
        if (prev.width !== entry.width || prev.height !== entry.height) {
          live.frame.updateSize(entry.width, entry.height);
          if (live.ctrl.resize) {
            try {
              live.ctrl.resize(entry.width, entry.height);
            } catch (err) {
              console.warn(`widget ${id} threw during resize:`, err);
            }
          }
        }

        // update collapsed state if changed
        if (prev.collapsed !== entry.collapsed) {
          live.frame.setCollapsed(entry.collapsed);
        }

        // update title if changed
        if (prev.title !== entry.title) {
          live.frame.setTitle(entry.title ?? "");
        }

        // snapshot the latest entry
        live.entry = { ...entry };
      }
    }

    this.updateStageBounds();
    this.updateLayerInfo();

    // enforce focus stack visibility — newly mounted or reconciled widgets
    // must stay hidden when another widget is maximized
    if (!this.focusStack.isEmpty) {
      const maximizedId = this.focusStack.peek()!.widgetId;
      for (const [id, live] of this.liveWidgets) {
        if (id === maximizedId || this.maximizedEscapees.has(id)) {
          live.frame.root.visible = true;
        } else {
          live.frame.root.visible = false;
        }
      }
    }

    this.updateBreadcrumbs();
  }

  /** update layer position info on all live widget frames */
  private updateLayerInfo(): void {
    for (const [id, live] of this.liveWidgets) {
      const info = this.store.getLayerInfo(id);
      live.frame.setLayerInfo(info.position, info.total);
    }
  }

  /** right-click handler: offers "Copy" when something is selected, and
   *  "Paste" (at the clicked world position, then clears the clipboard —
   *  a one-shot placement, unlike the repeatable Cmd/Ctrl+V) when the
   *  clipboard has content. shows nothing (but still suppresses the
   *  native browser menu) if neither applies. */
  private handleContextMenu = (e: MouseEvent): void => {
    e.preventDefault();

    const items: ContextMenuItem[] = [];
    const selected = this.inputRouter.selectedWidgetIds;
    const anyCopyable = [...selected].some((id) => {
      const entry = this.store.getWidget(id);
      return entry && isCopyable(this.registry, entry.type);
    });
    if (anyCopyable) {
      items.push({
        label: "copy",
        onSelect: () => {
          copySelectionToClipboard(this.store, this.registry, selected).catch((err) => {
            log.debug("widget-manager.clipboard", "copy failed (non-fatal):", err);
          });
        },
      });
    }
    if (hasClipboardContent()) {
      items.push({
        label: "paste",
        onSelect: () => {
          if (this.store.isLocalViewer() || !this.viewport) return;
          const at = this.viewport.screenToWorld(e.clientX, e.clientY);
          pasteClipboardIntoStore(this.store, this.crossCanvasRegistry, { at, clearAfter: true })
            .then((result) => {
              if (result.pasted.length > 0) this.inputRouter.selectWidgets(result.pasted);
            })
            .catch((err) => {
              log.debug("widget-manager.clipboard", "paste failed (non-fatal):", err);
            });
        },
      });
      items.push({
        label: "clear",
        onSelect: () => clearClipboard(),
      });
    }
    if (items.length === 0) return;
    showContextMenu(e.clientX, e.clientY, items, this.theme);
  };

  /** set lasso-active state on all live widget frames.
   *  when active, all widget content becomes inert with a dark overlay
   *  so the lasso pointer events aren't captured by widget content. */
  setLassoActive(active: boolean): void {
    for (const live of this.liveWidgets.values()) {
      live.frame.setLassoActive(active);
    }
  }

  /** return the map of all currently mounted widgets. */
  getLiveWidgets(): Map<string, LiveWidget> {
    return this.liveWidgets;
  }

  /**
   * expand the stage background to encompass all widgets with padding,
   * ensuring it always covers at least the base area (-2000,-2000 to 2000,2000)
   * so the lasso tool's hit area spans the full viewport.
   */
  private updateStageBounds(): void {
    if (this.liveWidgets.size === 0) return;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const live of this.liveWidgets.values()) {
      const e = live.entry;
      minX = Math.min(minX, e.x);
      minY = Math.min(minY, e.y);
      maxX = Math.max(maxX, e.x + e.width);
      maxY = Math.max(maxY, e.y + e.height);
    }

    const pad = 2000;
    let bgX = minX - pad;
    let bgY = minY - pad;
    let bgW = maxX - minX + pad * 2;
    let bgH = maxY - minY + pad * 2;

    // ensure minimum bounds cover the base area
    const baseMin = -2000;
    const baseMax = 2000;
    bgX = Math.min(bgX, baseMin);
    bgY = Math.min(bgY, baseMin);
    const bgRight = Math.max(bgX + bgW, baseMax);
    const bgBottom = Math.max(bgY + bgH, baseMax);
    bgW = bgRight - bgX;
    bgH = bgBottom - bgY;

    // only redraw if the bounds actually changed (avoid thrashing the graphics object)
    const last = this.lastStageBounds;
    if (last.x === bgX && last.y === bgY && last.w === bgW && last.h === bgH) return;
    this.lastStageBounds = { x: bgX, y: bgY, w: bgW, h: bgH };

    this.stageBg.clear();
    this.stageBg.rect(bgX, bgY, bgW, bgH);
    this.stageBg.fill({ color: this.theme.stageBg });
  }

  /**
   * unmount all widgets and unsubscribe from all listeners.
   * called during navigation teardown — per-widget docs are intentionally
   * kept alive so they're still available when the canvas is re-opened.
   */
  destroyAll(): void {
    // clean up focus stack
    this.focusStack.clear();
    this.maximizedEscapees.clear();

    this.canvasElement.removeEventListener("contextmenu", this.handleContextMenu);
    dismissContextMenu();

    for (const unsub of this.unsubs) {
      unsub();
    }
    this.unsubs = [];

    for (const id of [...this.liveWidgets.keys()]) {
      this.unmountWidget(id, false);
    }
  }
}
